import { Fragment, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

/**
 * Date picker in the reference admin's vue2-datepicker markup (.mx-datepicker / .mx-datepicker-main),
 * styled by styles/ref-mx-datepicker.css (verbatim .mx-* rules from the reference app.css).
 * Date / month / year panels, value shown as DD/MM/YYYY, popup appended to <body> under the input,
 * with the reference's mx-zoom-in-down enter transition. Dates for which `disabledDate(d)` is true
 * (default: after today, as on the reference report pages) cannot be picked.
 */
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const WEEK = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const p2 = (n) => String(n).padStart(2, "0");
const ymd = (d) => `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}`;
const dmy = (d) => (d ? `${p2(d.getDate())}/${p2(d.getMonth() + 1)}/${d.getFullYear()}` : "");
const sameDay = (a, b) => !!a && !!b && ymd(a) === ymd(b);
const endOfToday = () => {
  const t = new Date();
  t.setHours(23, 59, 59, 999);
  return t;
};
const afterToday = (d) => d > endOfToday();

const CalendarIcon = () => (
  <i className="mx-icon-calendar">
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1024 1024" width="1em" height="1em">
      <path d="M940.218182 107.054545h-209.454546V46.545455h-65.163636v60.50909H363.054545V46.545455H297.890909v60.50909H83.781818c-18.618182 0-32.581818 13.963636-32.581818 32.581819v805.236363c0 18.618182 13.963636 32.581818 32.581818 32.581818h861.090909c18.618182 0 32.581818-13.963636 32.581818-32.581818V139.636364c-4.654545-18.618182-18.618182-32.581818-37.236363-32.581819zM297.890909 172.218182V232.727273h65.163636V172.218182h307.2V232.727273h65.163637V172.218182h176.872727v204.8H116.363636V172.218182h181.527273zM116.363636 912.290909V442.181818h795.927273v470.109091H116.363636z"></path>
    </svg>
  </i>
);

export default function RefDatePicker({ value, onChange, name = "date", disabledDate = afterToday }) {
  const [open, setOpen] = useState(false);
  const [panel, setPanel] = useState("date");
  const [view, setView] = useState(() => new Date((value || new Date()).getFullYear(), (value || new Date()).getMonth(), 1));
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const [enter, setEnter] = useState("");
  const [leaving, setLeaving] = useState(false);
  const leaveTimer = useRef(null);

  // Close with the reference's mx-zoom-in-down leave transition (0.3s), then unmount
  const closePicker = () => {
    if (!open || leaving) return;
    setLeaving(true);
    leaveTimer.current = setTimeout(() => {
      setLeaving(false);
      setOpen(false);
    }, 300);
  };
  useEffect(() => () => clearTimeout(leaveTimer.current), []);
  const [text, setText] = useState(null); // text while the user is typing
  const wrapRef = useRef(null);
  const popRef = useRef(null);

  const openPicker = () => {
    if (open) return;
    const base = value || new Date();
    setView(new Date(base.getFullYear(), base.getMonth(), 1));
    setPanel("date");
    setOpen(true);
  };

  // Position under the input (document coords) and run the zoom-in-down enter transition
  useLayoutEffect(() => {
    if (!open || !wrapRef.current) return;
    const r = wrapRef.current.getBoundingClientRect();
    setPos({ top: r.bottom + window.scrollY, left: r.left + window.scrollX });
    setEnter("mx-zoom-in-down-enter mx-zoom-in-down-enter-active");
    const raf = requestAnimationFrame(() => setEnter("mx-zoom-in-down-enter-active mx-zoom-in-down-enter-to"));
    const done = setTimeout(() => setEnter(""), 320);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(done);
    };
  }, [open]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (wrapRef.current?.contains(e.target) || popRef.current?.contains(e.target)) return;
      closePicker();
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  });

  const pick = (d) => {
    if (disabledDate(d)) return;
    onChange?.(d);
    closePicker();
  };

  const y = view.getFullYear();
  const m = view.getMonth();
  const shift = (months) => setView(new Date(y, m + months, 1));

  const header = (buttons, label) => (
    <div className="mx-calendar-header">
      {buttons.map(([cls, fn], i) => (
        <Fragment key={cls}>
          {i > 0 && " "}
          <button type="button" className={`mx-btn mx-btn-text mx-btn-icon-${cls}`} onClick={fn}>
            <i className={`mx-icon-${cls}`} />
          </button>
        </Fragment>
      ))}{" "}
      <span className="mx-calendar-header-label">{label}</span>
    </div>
  );

  let body;
  if (panel === "date") {
    const first = new Date(y, m, 1);
    const start = new Date(y, m, 1 - first.getDay());
    const rows = Array.from({ length: 6 }, (_, r) =>
      Array.from({ length: 7 }, (_, c) => new Date(start.getFullYear(), start.getMonth(), start.getDate() + r * 7 + c))
    );
    body = (
      <div className="mx-calendar mx-calendar-panel-date">
        {header(
          [
            ["double-left", () => shift(-12)],
            ["left", () => shift(-1)],
            ["double-right", () => shift(12)],
            ["right", () => shift(1)],
          ],
          <>
            <button type="button" className="mx-btn mx-btn-text mx-btn-current-month" onClick={() => setPanel("month")}>
              {MONTHS[m]}
            </button>
            <button type="button" className="mx-btn mx-btn-text mx-btn-current-year" onClick={() => setPanel("year")}>
              {y}
            </button>
          </>
        )}{" "}
        <div className="mx-calendar-content">
          <table className="mx-table mx-table-date">
            <thead>
              <tr>
                {WEEK.map((w) => (
                  <th key={w}>{w}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, r) => (
                <tr key={r} className="mx-date-row">
                  {row.map((d, c) => {
                    const cls = ["cell"];
                    if (d.getMonth() !== m) cls.push("not-current-month");
                    if (sameDay(d, new Date())) cls.push("today");
                    if (sameDay(d, value)) cls.push("active");
                    if (disabledDate(d)) cls.push("disabled");
                    return (
                      <td key={c} data-row-col={`${r},${c}`} title={ymd(d)} className={cls.join(" ")} onClick={() => pick(d)}>
                        <div>{d.getDate()}</div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  } else if (panel === "month") {
    body = (
      <div className="mx-calendar mx-calendar-panel-month">
        {header(
          [
            ["double-left", () => shift(-12)],
            ["double-right", () => shift(12)],
          ],
          <button type="button" className="mx-btn mx-btn-text" onClick={() => setPanel("year")}>
            {y}
          </button>
        )}{" "}
        <div className="mx-calendar-content">
          <table className="mx-table mx-table-month">
            <tbody>
              {[0, 1, 2, 3].map((r) => (
                <tr key={r}>
                  {[0, 1, 2].map((c) => {
                    const mi = r * 3 + c;
                    const active = value && value.getFullYear() === y && value.getMonth() === mi;
                    return (
                      <td
                        key={mi}
                        data-month={mi}
                        className={active ? "cell active" : "cell"}
                        onClick={() => {
                          setView(new Date(y, mi, 1));
                          setPanel("date");
                        }}
                      >
                        <div>{MONTHS[mi]}</div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  } else {
    const decade = Math.floor(y / 10) * 10;
    body = (
      <div className="mx-calendar mx-calendar-panel-year">
        {header(
          [
            ["double-left", () => setView(new Date(y - 10, m, 1))],
            ["double-right", () => setView(new Date(y + 10, m, 1))],
          ],
          <>
            <span>{decade}</span> <span className="mx-calendar-decade-separator" /> <span>{decade + 9}</span>
          </>
        )}{" "}
        <div className="mx-calendar-content">
          <table className="mx-table mx-table-year">
            <tbody>
              {[0, 1, 2, 3, 4].map((r) => (
                <tr key={r}>
                  {[0, 1].map((c) => {
                    const yr = decade + r * 2 + c;
                    const active = value && value.getFullYear() === yr;
                    return (
                      <td
                        key={yr}
                        data-year={yr}
                        className={active ? "cell active" : "cell"}
                        onClick={() => {
                          setView(new Date(yr, m, 1));
                          setPanel("month");
                        }}
                      >
                        <div>{yr}</div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-datepicker" ref={wrapRef}>
      <div className="mx-input-wrapper">
        <input
          name={name}
          type="text"
          autoComplete="off"
          placeholder=""
          className="mx-input"
          value={text ?? dmy(value)}
          onChange={(e) => {
            setText(e.target.value);
            const mt = e.target.value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
            if (mt) {
              const d = new Date(+mt[3], +mt[2] - 1, +mt[1]);
              if (d.getDate() === +mt[1] && !disabledDate(d)) {
                onChange?.(d);
                setView(new Date(d.getFullYear(), d.getMonth(), 1));
              }
            }
          }}
          onBlur={() => setText(null)}
          onFocus={openPicker}
          onClick={openPicker}
        />
        <CalendarIcon />
      </div>
      {open &&
        createPortal(
          <div
            ref={popRef}
            className={`mx-datepicker-main mx-datepicker-popup ${leaving ? "mx-zoom-in-down-leave-active mx-zoom-in-down-leave-to" : enter}`.trim()}
            style={{ top: `${pos.top}px`, left: `${pos.left}px`, position: "absolute" }}
          >
            <div className="mx-datepicker-content">
              <div className="mx-datepicker-body">{body}</div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
